import React, {
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';
import { VictoryCursorContainerProps } from 'victory';
import { useEventCallback } from '@mui/material';
import { ISelectionTooltipOptions } from './SelectionTooltip.models';
import SelectionTooltip from '@ui/ui/victory/SelectionTooltip/SelectionTooltip';
import styles from './SelectionTooltip.module.scss';

/**
 * Функция для получения выбранного значения
 */
function getSelectedItem(data: Record<string, any>, xValue: number, stacked: boolean) {
    if (stacked) {
        const item = data?.[xValue-1];

        return {
            x: xValue,
            ...item,
        };
    }

    // Если переданные значения просто списком, то работаем стандартно
    if (Array.isArray(data)) {
        return data.find(item => (item as any).x === xValue);
    }

    // Если работа идет с больше, чем одним значением
    let result = {};

    // Получаем текущие значения, над которыми находится курсор
    const values = Object.values(data).map((value: any[]) => {
        return value.find(item => item.x === xValue);
    });

    if (values[0] === undefined) {
        // Если под курсором нет никаких значений, то отдаем null
        result = null;
    } else {
        // Форматируем значения в такие, чтобы BarChart мог их понять
        Object.keys(data).forEach((key, index) => {
            result[key] = values[index];
        });
    }

    return result;
}

/**
 * Функция для получения максимального значения по Y для выбранного элемента
 */
function getMaxYFromSelectedItem(selectedItem: any | Record<string, any>, stacked: boolean, stackedHideSeries: string[]) {
    if (stacked) {
        const defaultExcludes = ['x', 'date', 'start_date', 'end_date'];

        return Object.entries(selectedItem)
            .filter(([key, value]) =>
                !defaultExcludes?.includes(key) &&
                !stackedHideSeries?.includes(key) &&
                typeof value === 'number'
            )
            .reduce((sum, [, value]) => sum + (value as number), 0);
    }

    let result = 0;

    Object.values(selectedItem).forEach((item) => {
        const yValue = (item as any).y;

        result = result < yValue ? yValue : result;
    });

    return result;
}

const TOOLTIP_MARGIN = 5;

/**
 * Хук для использования тултипа для графиков Victory
 *
 * @example
 * const selectionContainerProps = useSelectionTooltipContainer({
 *         lineWidth: columnWidth,
 *         data: formattedData,
 *         tooltipTitle: (selectedValue) => {
 *             const quarter = moment(selectedValue?.title).quarter();
 *             const year = moment(selectedValue?.title).year();
 *
 *             return `${quarter} квартал ${year}, ${QUARTERS_MAP[quarter]}`;
 *         },
 *         tooltipContent: selectedValue => {
 *             return [
 *                 {
 *                     title: 'Кредитный портфель',
 *                     value: `${selectedValue.y} млрд`,
 *                 },
 *             ];
 *         },
 *     })
 *
 * return (
 *     <VictoryChart
 *         containerComponent={(
 *             <VictoryCursorContainer {...selectionContainerProps} />
 *         )}
 *     />
 * )
 */
export function useSelectionTooltipContainer({
    lineWidth,
    data,
    stacked,
    tooltipTitle,
    tooltipContent,
    stackedHideSeries,
}: ISelectionTooltipOptions) {
    const width = useRef(0);

    const prevXValue = useRef(null);
    // Линия для тултипа
    const selectionTooltipRef = useRef<SVGLineElement>(null);

    // Родительский компонент тултипа
    const tooltipRef = useRef<HTMLDivElement>(null);

    // Название для тултипа
    const tooltipTitleRef = useRef<HTMLDivElement>(null);

    // Контент для тултипа
    const tooltipContentRef = useRef<HTMLDivElement>(null);

    const didCursorMoved = useEventCallback((value: number, cursorProps: VictoryCursorContainerProps) => {
        /**
         * Инициализация элементов для тултипа
         */
        if (tooltipRef.current === null) {
            const tooltipDiv = document.createElement('div');
            const tooltipTitleDiv = document.createElement('div');
            const tooltipContentDiv = document.createElement('div');

            tooltipTitleDiv.className = styles['tooltip-title'];
            tooltipDiv.className = styles['tooltip-container'];
            tooltipContentDiv.className = styles['tooltip-content'];

            tooltipRef.current = tooltipDiv;
            tooltipTitleRef.current = tooltipTitleDiv;
            tooltipContentRef.current = tooltipContentDiv;

            document.body.appendChild(tooltipDiv);

            tooltipDiv.appendChild(tooltipTitleDiv);
            tooltipDiv.appendChild(tooltipContentDiv);
        }

        // Ищем максимальное доступное значение
        const maxValue = Array.isArray(data) ? data.length : Object.values(data).reduce((max, arr) => Math.max(max, arr.length), 0);

        /**
         * При выходе из области графика убираем тултип
         */
        if (value === null || !data || value < 0.5 || Math.round(value) > maxValue) {
            selectionTooltipRef.current.style.display = 'none';

            if (tooltipRef.current !== null) {
                tooltipRef.current.style.display = 'none';
                tooltipRef.current.style.transition = 'all 0s ease-out';
            }

            return;
        }

        /**
         * Показываем тултип, когда пользователь навелся на график
         */
        selectionTooltipRef.current.style.display = 'block';
        tooltipRef.current.style.display = 'block';
        tooltipRef.current.style.transition = 'all 0.2s ease-out';

        const xValue = Math.round(value);

        /**
         * Объявления для упрощенного пользования элементами
         */
        const selectionTooltipElement = selectionTooltipRef.current;
        const tooltipElement = tooltipRef.current;
        const tooltipTitleElement = tooltipTitleRef.current;
        const tooltipContentElement = tooltipContentRef.current;

        /**
         * При неизменном значении больше ничего не делаем(для оптимизации)
         */
        if (JSON.stringify(prevXValue.current) === JSON.stringify(xValue)) {
            return;
        }
        prevXValue.current = xValue;

        // Передвигаем линию тултипа
        selectionTooltipElement.style.transform = `translateX(${Math.round(cursorProps.scale.x(xValue))}px)`;

        // Получаем данные для отображения в тултипе
        const selectedItem: any | Record<string, any> = getSelectedItem(data, xValue, stacked);

        // Получаем текст для названия из данных
        tooltipTitleElement.innerText = tooltipTitle(selectedItem);

        // Получаем текстовый HTML для отображения основного контента для тултипа
        tooltipContentElement.innerHTML = tooltipContent(selectedItem);

        // Проверяем, нужно ли инвертировать позицию тултипа, чтобы корректно отображать у краев
        const invertPosition = cursorProps.scale.x(xValue) > cursorProps.width / 2;

        const yValue = getMaxYFromSelectedItem(selectedItem, stacked, stackedHideSeries);

        /**
         * Получаем общую позицию тултипа
         */
        const parentBoundingClientRect = selectionTooltipElement.parentElement.getBoundingClientRect();
        const topValue = parentBoundingClientRect.top + window.scrollY + cursorProps.scale.y(yValue);
        const leftValue = parentBoundingClientRect.left + cursorProps.scale.x(xValue + (invertPosition ? -0.5 : 0.5));

        /**
         * Двигаем тултип за линией
         */
        tooltipElement.style.left = `${Math.round(leftValue - (invertPosition ? tooltipElement.getBoundingClientRect().width+TOOLTIP_MARGIN : -TOOLTIP_MARGIN))}px`;
        tooltipElement.style.top = `${Math.round(topValue)}px`;

        /**
         * Инвертирование позиции тултипа
         */

        // Получаем ширину графика
        if (width.current !== cursorProps.width) {
            width.current = cursorProps.width;
        }
    });

    useLayoutEffect(() => {
        if (!selectionTooltipRef.current) {
            return;
        }

        selectionTooltipRef.current.style.display = 'none';
    }, [selectionTooltipRef.current]);

    return useMemo(() => ({
        onCursorChange: didCursorMoved,
        cursorComponent: (
            <SelectionTooltip
                lineWidth={lineWidth}
                ref={selectionTooltipRef}
            />),
        cursorDimension: 'x',
        defaultCursorValue: 0,
    } as const), [didCursorMoved, lineWidth]);
}
